import { useRef, useState } from 'react';
import { getAccountByIdOrSfId, getAccounts as cachedAccounts } from '../../../store/Accounts';
import { getAccounts, getAccountScope } from '../../../store/User';
import { Account, InventoryAsset } from '../../../types';
import { sameAccount } from '../../../utils/accountIds';

const knownAccounts = (): Account[] => [...(getAccountScope() || []), ...(getAccounts() || []), ...cachedAccounts()];

const accountOfLocation = (accountId: string): Account | undefined =>
  knownAccounts().find((account) => sameAccount(account.id, accountId));

/** The known account a service belongs to, by its portal id or its Salesforce id. */
export const accountOfService = (service: InventoryAsset): Account | undefined =>
  knownAccounts().find(
    (account) =>
      sameAccount(account.id, service.account_upstack_id) ||
      sameAccount(account.id, service.account_id) ||
      (account.sfId !== undefined && account.sfId === service.account_id)
  );

export const directoryAccount = async (accountId: string): Promise<Account | undefined> => {
  try {
    return await getAccountByIdOrSfId(accountId);
  } catch {
    return undefined;
  }
};

/** The account a case is filed under: one from the scope when it holds it, else the directory's answer, latest lookup wins. */
export const useCaseAccount = (initial: () => Account | undefined, onMiss: () => void) => {
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(initial);
  const [accountPending, setAccountPending] = useState<boolean>(false);
  const lookup = useRef<number>(0);

  const startLookup = () => ++lookup.current;

  const adopt = (account: Account) => {
    setSelectedAccount(account);
    setAccountPending(false);
  };

  const settle = (token: number, account: Account | undefined) => {
    if (token !== lookup.current) return;
    setSelectedAccount(account);
    setAccountPending(false);
    if (!account) onMiss();
  };

  const adoptLocation = async (accountId: string) => {
    const token = startLookup();
    const known = accountOfLocation(accountId);
    if (known) return adopt(known);
    setAccountPending(true);
    settle(token, await directoryAccount(accountId));
  };

  const adoptService = async (service: InventoryAsset, locationAccountId: string) => {
    const token = startLookup();
    const known = accountOfService(service);
    if (known) return adopt(known);
    setAccountPending(true);
    const named = service.account_upstack_id || service.account_id;
    const account = named
      ? await directoryAccount(named)
      : accountOfLocation(locationAccountId) || (await directoryAccount(locationAccountId));
    settle(token, account);
  };

  const dropLookup = () => {
    startLookup();
    setAccountPending(false);
  };

  const reset = () => {
    dropLookup();
    setSelectedAccount(undefined);
  };

  return { selectedAccount, setSelectedAccount, accountPending, adoptLocation, adoptService, dropLookup, reset };
};
